import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  type Node,
  type Edge,
  type Connection,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  type NodeChange,
  type EdgeChange,
} from "@xyflow/react";
import { useApi } from "../../lib/api";
import { useTenant } from "../../lib/tenant";
import Hint from "../../components/Hint";
import { useNavigate } from "react-router-dom";
import { DeepLink } from "../../components/DeepLink";
import { dashboardUrl } from "../../lib/deeplinks";

/** Antwortformat von GET /v1/driver-trees/{id}/nodes (dev-v122+). */
type DriverNode = {
  id: string;
  metric_id: string | null;
  label: string;
  position?: { x: number; y: number } | null;
  /** additiv angereichert vom Backend, wenn metric_id gesetzt ist */
  value?: number | null;
  value_ts?: string | null;
  value_unit?: string | null;
  value_quality?: string | null;
  target_value?: number | null;
  attainment_pct?: number | null;
  target_reached?: boolean | null;
};
type DriverEdge = {
  id: string;
  from_node: string;
  to_node: string;
  weight?: number;
  confidence?: number;
  rationale?: string;
};

const OPENPROJECT_BASE = "https://project.impulse.kim/work_packages";

/** Arbeitspaket-Nummern aus dem Knoten-Label ziehen ("... OP-WP 298/303/299"). */
export function workPackageIds(label: string): string[] {
  const m = label.match(/OP-WP\s*([0-9/\s,]+)/i);
  if (!m) return [];
  return m[1].split(/[/,\s]+/).map((s) => s.trim()).filter((s) => /^[0-9]+$/.test(s));
}

/** Label ohne den technischen WP-Anhang. */
function cleanLabel(label: string): string {
  return label.replace(/\s*·?\s*OP-WP\s*[0-9/\s,]+/i, "").trim();
}

function fmt(v: number, unit?: string | null): string {
  const u = unit ?? "";
  if (u === "%") return v.toFixed(1) + " %";
  if (u === "EUR") return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  if (u === "Index") return v.toFixed(0);
  return `${v.toLocaleString("de-DE", { maximumFractionDigits: 2 })} ${u}`.trim();
}

function attClass(n: DriverNode): string {
  if (n.target_reached) return "gut";
  const p = n.attainment_pct;
  if (p == null) return "neutral";
  if (p >= 90) return "gut";
  if (p >= 70) return "mittel";
  return "kritisch";
}

/** Ein Knoten: Titel, Ist-Wert, Ziel, Zielerreichung, OpenProject-Links. */
function KpiTreeNode({ data }: { data: any }) {
  const n = data.node as DriverNode;
  const wps = workPackageIds(n.label);
  const hasValue = n.value != null;
  const isMetric = Boolean(n.metric_id);
  const cls = attClass(n);
  return (
    <div className={`dtn kind-${isMetric ? "kpi" : wps.length ? "handlungsfeld" : "wurzel"} att-${cls}`}>
      <Handle type="target" position={Position.Top} />
      <div className="dtn-kind">{isMetric ? "Kennzahl" : wps.length ? "Handlungsfeld" : "Wertbeitrag"}</div>
      <div className="dtn-label">{cleanLabel(n.label)}</div>

      {isMetric && (
        hasValue ? (
          <div className="dtn-metrics">
            <div className="dtn-row">
              <span>Ist</span>
              <strong>{fmt(n.value as number, n.value_unit)}</strong>
            </div>
            {n.target_value != null && (
              <div className="dtn-row">
                <span>Ziel</span>
                <strong>{fmt(n.target_value, n.value_unit)}</strong>
              </div>
            )}
            {n.attainment_pct != null && (
              <>
                <div className="dtn-bar"><span style={{ width: `${Math.max(0, Math.min(100, n.attainment_pct))}%` }} /></div>
                <div className="dtn-att">
                  {n.attainment_pct.toFixed(1)} % Zielerreichung {n.target_reached ? "✓" : ""}
                </div>
              </>
            )}
            {n.value_ts && (
              <div className="dtn-ts">Stand {new Date(n.value_ts).toLocaleDateString("de-DE")}</div>
            )}
          </div>
        ) : (
          <div className="dtn-metrics empty">
            <div className="dtn-att">Kennzahl definiert, aber noch kein Messwert</div>
          </div>
        )
      )}

      {isMetric && (
        <div className="dtn-links">
          {/* BEN-92: einheitlicher Deep-Link vom Treiber-Knoten ins Dashboard */}
          <DeepLink
            href={dashboardUrl("cockpit")}
            label="Dashboard"
            kind="dashboard"
            title="Diese Kennzahl im OpenSearch-Cockpit ansehen"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {wps.length > 0 && (
        <div className="dtn-wps">
          <span>Arbeitspakete</span>
          {wps.map((wp) => (
            <a
              key={wp}
              href={`${OPENPROJECT_BASE}/${wp}`}
              target="_blank"
              rel="noreferrer"
              className="dtn-wp"
              title={`Arbeitspaket ${wp} in OpenProject öffnen`}
              onClick={(e) => e.stopPropagation()}
            >
              #{wp}
            </a>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const nodeTypes: any = { kpiNode: KpiTreeNode };

type Props = {
  treeId: string;
  readOnly?: boolean;
  onProposeNode?: (node: Partial<DriverNode>) => void;
  onProposeEdge?: (edge: Partial<DriverEdge>) => void;
};

/**
 * Driver-Tree-Canvas auf /v1/driver-trees/{id}/nodes|edges.
 *
 * Behobene Fehler (2026-07-12):
 *  - Positionen wurden aus n.x / n.y gelesen; die API liefert n.position.{x,y}
 *    -> alle Knoten lagen uebereinander auf (0,0). Jetzt korrekt.
 *  - Knoten zeigten nur das Label; Ist-Wert, Ziel und Zielerreichung lagen
 *    ungenutzt in der Antwort. Jetzt sichtbar.
 *  - Arbeitspaket-Nummern im Label verlinken jetzt nach OpenProject.
 */
export function DriverTreeCanvas({ treeId, readOnly = false, onProposeNode }: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [raw, setRaw] = useState<DriverNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ns, es] = await Promise.all([
        api.get<DriverNode[]>(`/driver-trees/${treeId}/nodes`),
        api.get<DriverEdge[]>(`/driver-trees/${treeId}/edges`),
      ]);
      setRaw(ns);
      setNodes(
        ns.map((n) => ({
          id: n.id,
          type: "kpiNode",
          data: { node: n },
          position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
        })),
      );
      setEdges(
        es.map((e) => ({
          id: e.id,
          source: e.from_node,
          target: e.to_node,
          label: e.confidence != null ? `${(e.confidence * 100).toFixed(0)} % Konfidenz` : undefined,
          labelStyle: { fontSize: 10, fill: "var(--muted, #9aa)" },
          style: { strokeWidth: Math.max(1, (e.weight ?? 1) * 1.5) },
          markerEnd: { type: MarkerType.ArrowClosed },
          animated: (e.confidence ?? 1) < 0.4,
          title: e.rationale,
        })),
      );
      setError(null);
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string };
      setError(`${e.status ?? "Fehler"}: ${e.message ?? "unbekannt"}`);
    } finally {
      setLoading(false);
    }
  }, [api, effectiveTenantId, treeId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const onConnect = useCallback(
    async (conn: Connection) => {
      if (readOnly || !conn.source || !conn.target) return;
      setEdges((eds) => addEdge({ ...conn, id: `tmp_${Math.random()}` }, eds));
      try {
        await api.post(`/driver-trees/${treeId}/edges`, {
          from_node: conn.source, to_node: conn.target, weight: 1, confidence: 0.5,
        });
      } finally {
        await refresh();
      }
    },
    [api, refresh, readOnly, treeId],
  );

  const onAddNode = useCallback(
    async (metric_id: string, label: string) => {
      if (readOnly) return;
      const created = await api.post<DriverNode>(`/driver-trees/${treeId}/nodes`, {
        metric_id, label, x: Math.random() * 400, y: Math.random() * 300,
      });
      onProposeNode?.(created);
      await refresh();
    },
    [api, onProposeNode, refresh, readOnly, treeId],
  );

  const stats = useMemo(() => {
    const withValue = raw.filter((n) => n.value != null).length;
    const withMetric = raw.filter((n) => n.metric_id).length;
    return { nodes: nodes.length, edges: edges.length, withValue, withMetric };
  }, [nodes.length, edges.length, raw]);

  return (
    <div className="driver-tree" style={{ height: "100%", display: "grid", gridTemplateRows: "auto auto 1fr", gap: 8 }}>
      <div className="dt-toolbar">
        <span className="muted small">
          {stats.nodes} Knoten · {stats.edges} Kanten · {stats.withValue} von {stats.withMetric} Kennzahlen mit Ist-Wert
        </span>
        <span className="grow" />
        <button
          className="btn"
          disabled={readOnly || loading}
          onClick={() => {
            const m = window.prompt("Kennzahl-ID (metric_...), leer lassen für reinen Struktur-Knoten:");
            const lbl = window.prompt("Beschriftung des Knotens:");
            if (!lbl) return;
            void onAddNode(m ?? "", lbl);
          }}
        >
          + Knoten
        </button>
        <button
          className="btn"
          disabled={loading}
          title="Oeffnet den KI-Berater (Perspektive Berater) mit dem Kontext dieses Treiberbaums und laesst zusätzliche Werttreiber/Knoten vorschlagen."
          onClick={() =>
            navigate(
              "/app/advisor?mode=strategy&persona=Berater&msg=" +
                encodeURIComponent(
                  "Analysiere den aktuellen Treiberbaum und schlage 3 zusätzliche Werttreiber oder Knoten vor, die wir ergaenzen sollten. Nenne je Vorschlag die Kennzahl und den erwarteten Effekt.",
                ),
            )
          }
        >
          KI-Vorschläge (Berater)
        </button>
        <button className="btn ghost" onClick={refresh} disabled={loading}>
          {loading ? "Lade …" : "Neu laden"}
        </button>
      </div>

      <div className="dt-legend-bar">
        <span className="dt-lg wurzel">Wertbeitrag</span>
        <span className="dt-lg handlungsfeld">Handlungsfeld (mit Arbeitspaketen)</span>
        <span className="dt-lg kpi">Kennzahl (mit Ist / Ziel)</span>
        <span className="grow" />
        <span className="muted small">
          Farbe = Zielerreichung: gruen ab 90 %, gelb ab 70 %, rot darunter.
          <Hint term="driver_tree" />
        </span>
      </div>

      {error && <div className="error">Driver-Tree konnte nicht geladen werden ({error}).</div>}
      {!error && !loading && nodes.length === 0 && (
        <div className="empty-friendly">
          <strong>Dieser Baum hat noch keine Knoten.</strong>
          <p className="muted">
            Legen Sie oben einen Knoten an - oder wählen Sie einen anderen Baum. Ein Driver-Tree beantwortet
            die Frage: Welcher Hebel wirkt auf welche Kennzahl?
          </p>
        </div>
      )}

      <div style={{ height: "100%", minHeight: 520 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitView
          minZoom={0.2}
          maxZoom={1.6}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} />
          <Controls showInteractive={!readOnly} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
    </div>
  );
}
